import React, { useState } from "react";
import styled from "styled-components";
import { Formik, Form, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import EmployerUser from "../../apiResources/EmployerUser";
import Employer from "../../apiResources/Employer";
import { FileUploadField } from "../library/Form";
import TextLink from "../library/TextLink";
import SettingLabel from "../settings/SettingLabel";

type FormValues = {
  employerProfileImage?: string;
  employerProfileImage2?: string;
  employerProfileImage3?: string;
};

const BannerImageSettingsOption = (props: {
  user: EmployerUser;
  company: Employer;
  label: string;
  disabled?: boolean;
  field:
    | "employerProfileImage"
    | "employerProfileImage2"
    | "employerProfileImage3";
}) => {
  const { user, company, field, label, disabled } = props;

  const [isEditing, setIsEditing] = useState(false);

  const [imgUrl, setImgUrl] = useState<string>(company[field + "Url"]);

  const hasValue = !imgUrl.includes("/original/missing.png");

  const submit = async (values, helpers: FormikHelpers<FormValues>) => {
    try {
      company.assignAttributes(values);
      const success = await company.save();

      if (success) {
        setImgUrl(company[field + "Url"]);
        setIsEditing(false);
      } else {
        helpers.setErrors({
          [field]: company.errors[field]?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      helpers.setErrors({
        [field]: "Something went wrong uploading the image",
      });
    } finally {
      company.rollback();
    }
  };

  if (hasValue && !isEditing) {
    return (
      <Wrapper>
        <Header>
          <SettingLabel>{label}</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={() => setIsEditing(true)}>
              Edit
            </TextLink>
          )}
        </Header>
        <DisplayImage src={imgUrl} />
      </Wrapper>
    );
  }

  return (
    <div>
      <Header>
        <SettingLabel>{label}</SettingLabel>
        {isEditing && (
          <TextLink onClick={() => setIsEditing(false)}>Cancel</TextLink>
        )}
      </Header>

      {user.isAdmin && (
        <Formik initialValues={{ [field]: "" }} onSubmit={submit}>
          {({ submitForm, isSubmitting }) => (
            <Form>
              <FileUploadField
                name={field}
                label={label}
                hiddenLabel
                accept="image/png,image/jpeg,image/webp"
                acceptHint="Please upload an image"
                onChange={() => submitForm()}
              >
                {isSubmitting && (
                  <UploadStatus>Uploading image...</UploadStatus>
                )}
              </FileUploadField>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

const DisplayImage = styled.img`
  display: block;
  height: 200px;
  width: 350px;
  object-fit: cover;
  margin-bottom: 0.5rem;
`;

const Wrapper = styled.div`
  margin-bottom: 1rem;
`;

const UploadStatus = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default BannerImageSettingsOption;
